import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Upload,
  Download,
  AlertTriangle,
  Award,
  RefreshCw,
  Plus,
  Eye
} from 'lucide-react'
import apiService from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { getUserType } from '../../utils/userPermissions'
import { toast } from 'react-toastify'

interface ComplianceDocument {
  id: string
  name: string
  type: string
  status: 'valid' | 'expired' | 'pending' | 'missing'
  expiryDate?: string
  uploadDate?: string
  fileUrl?: string
  description: string
}

interface ComplianceRequirement {
  id: string
  name: string
  description: string
  required: boolean
  category: 'certification' | 'insurance' | 'security' | 'financial' | 'other'
  status: 'compliant' | 'non-compliant' | 'pending'
  documents: ComplianceDocument[]
}

const CompliancePage: React.FC = () => {
  const { user } = useAuthStore()
  const userType = getUserType(user)
  const [requirements, setRequirements] = useState<ComplianceRequirement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState<string | null>(null)

  useEffect(() => {
    fetchComplianceData()
  }, [])

  const fetchComplianceData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiService.get('/api/gov/compliance') as any
      
      if (response?.success) {
        setRequirements(response.data || [])
      } else {
        // For demo purposes, use mock data if API fails
        setRequirements(getMockComplianceData())
      }
    } catch (err) {
      console.error('Error fetching compliance data:', err)
      setError('Failed to fetch compliance data')
      // Use mock data as fallback
      setRequirements(getMockComplianceData())
    } finally {
      setLoading(false)
    }
  }

  const getMockComplianceData = (): ComplianceRequirement[] => {
    return [
      {
        id: '1',
        name: 'CMMC Level 2 Certification',
        description: 'Cybersecurity Maturity Model Certification Level 2',
        required: true,
        category: 'certification',
        status: 'compliant',
        documents: [
          {
            id: '1',
            name: 'CMMC Level 2 Certificate',
            type: 'certification',
            status: 'valid',
            expiryDate: '2025-12-31',
            uploadDate: '2024-01-15',
            fileUrl: '#',
            description: 'Official CMMC Level 2 certification document'
          }
        ]
      },
      {
        id: '2',
        name: 'General Liability Insurance',
        description: 'Minimum $1M general liability insurance coverage',
        required: true,
        category: 'insurance',
        status: 'compliant',
        documents: [
          {
            id: '2',
            name: 'General Liability Insurance Policy',
            type: 'insurance',
            status: 'valid',
            expiryDate: '2025-06-30',
            uploadDate: '2024-06-15',
            fileUrl: '#',
            description: 'Current general liability insurance policy'
          }
        ]
      },
      {
        id: '3',
        name: 'Security Clearance',
        description: 'Active security clearance for classified work',
        required: false,
        category: 'security',
        status: 'pending',
        documents: [
          {
            id: '3',
            name: 'Security Clearance Application',
            type: 'security',
            status: 'pending',
            uploadDate: '2024-11-01',
            fileUrl: '#',
            description: 'Security clearance application in progress'
          }
        ]
      },
      {
        id: '4',
        name: 'ISO 27001 Certification',
        description: 'Information Security Management System certification',
        required: false,
        category: 'certification',
        status: 'non-compliant',
        documents: []
      }
    ]
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'non-compliant':
        return <XCircle className="w-5 h-5 text-red-500" />
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'non-compliant':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'certification':
        return <Award className="w-5 h-5" />
      case 'insurance':
        return <Shield className="w-5 h-5" />
      case 'security':
        return <Shield className="w-5 h-5" />
      case 'financial':
        return <FileText className="w-5 h-5" />
      default:
        return <FileText className="w-5 h-5" />
    }
  }

  const handleFileUpload = async (requirementId: string, file: File) => {
    try {
      setUploading(requirementId)
      
      const formData = new FormData()
      formData.append('file', file)
      formData.append('requirementId', requirementId)
      
      const response = await apiService.post('/api/gov/compliance/upload', formData) as any
      
      if (response?.success) {
        toast.success('Document uploaded successfully')
        fetchComplianceData()
      } else {
        toast.error('Failed to upload document')
      }
    } catch (err) {
      console.error('Error uploading file:', err)
      toast.error('Failed to upload document')
    } finally {
      setUploading(null)
    }
  }

  const ComplianceCard: React.FC<{ requirement: ComplianceRequirement }> = ({ requirement }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
            {getCategoryIcon(requirement.category)}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {requirement.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {requirement.description}
            </p>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(requirement.status)}`}>
                {getStatusIcon(requirement.status)}
                <span className="ml-1">{requirement.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              </span>
              {requirement.required && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  Required
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Documents</h4>
        
        {requirement.documents.length === 0 ? (
          <div className="text-center py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No documents uploaded</p>
            <label className="btn btn-primary cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFileUpload(requirement.id, file)
                }}
                disabled={uploading === requirement.id}
              />
            </label>
          </div>
        ) : (
          <div className="space-y-2">
            {requirement.documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{doc.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Uploaded {doc.uploadDate && new Date(doc.uploadDate).toLocaleDateString()}
                      {doc.expiryDate && ` • Expires ${new Date(doc.expiryDate).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    doc.status === 'valid' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    doc.status === 'expired' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {doc.status}
                  </span>
                  <button className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            
            <div className="pt-2">
              <label className="btn btn-outline cursor-pointer">
                <Plus className="w-4 h-4 mr-2" />
                Add Document
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload(requirement.id, file)
                  }}
                  disabled={uploading === requirement.id}
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to="/government"
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Compliance Management
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {userType === 'AGENCY' 
                  ? 'Manage your agency compliance requirements and certifications'
                  : 'Track your compliance status and required certifications for government contracts'
                }
              </p>
            </div>
            
            <div className="mt-4 lg:mt-0">
              <button
                onClick={fetchComplianceData}
                disabled={loading}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Compliance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Compliant</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {requirements.filter(r => r.status === 'compliant').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {requirements.filter(r => r.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Non-Compliant</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {requirements.filter(r => r.status === 'non-compliant').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Requirements</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {requirements.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Requirements */}
        {loading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <RefreshCw className="w-8 h-8 text-primary-600 animate-spin mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading compliance data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchComplianceData}
                className="btn btn-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : requirements.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Shield className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">No compliance requirements found.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {requirements.map((requirement) => (
              <ComplianceCard key={requirement.id} requirement={requirement} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CompliancePage